/**
 * The arithmetic behind the /feedback KPI strip.
 *
 * Pure, and out of the component, so the definitions can be tested directly —
 * "what counts as open" is the sort of thing that quietly drifts between a
 * tile and the grid beside it, and the only defence is one place that says so.
 */

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Statuses that take a ticket off the queue.
 *
 * Mirrors `FEEDBACK_TERMINAL_STATUSES` in de_api/broker_deals/router.py,
 * which is what the list endpoint's `open_only` filters on. Kept in sync by
 * hand: the vocabulary is CHECK-constrained in the database, so it changes
 * with a migration or not at all.
 */
export const TERMINAL_STATUSES = ["resolved", "wont_fix", "duplicate"];

/** @param {object} ticket @returns {boolean} */
export const isOpenTicket = (ticket) =>
  !TERMINAL_STATUSES.includes(ticket?.status);

/**
 * Age of a ticket in whole days, measured from the reporter's own clock —
 * the same stamp the grid's "Reported" column prints, so the number in the
 * tile and the date in the row cannot disagree.
 *
 * @param {object} ticket
 * @param {number} nowMs
 * @returns {number|null} null when the ticket carries no usable timestamp
 */
export const ticketAgeDays = (ticket, nowMs = Date.now()) => {
  const raw = ticket?.client_reported_at || ticket?.created_at;
  if (!raw) return null;
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.floor((nowMs - ms) / MS_PER_DAY));
};

/**
 * All six KPI figures in one pass over the queue.
 *
 * Every count except `open` itself is a count of OPEN tickets — a resolved
 * critical bug is not something anyone needs to be told about.
 *
 * @param {Array}  rows   the whole scoped queue, not the filtered view
 * @param {number} nowMs
 * @returns {{open:number, untriaged:number, inProgress:number,
 *            openBugs:number, openUrgent:number, oldestOpen:number|null}}
 */
export const feedbackStats = (rows = [], nowMs = Date.now()) => {
  let open = 0;
  let untriaged = 0;
  let inProgress = 0;
  let openBugs = 0;
  let openUrgent = 0;
  let oldestOpen = null;

  for (const t of Array.isArray(rows) ? rows : []) {
    if (!isOpenTicket(t)) continue;
    open += 1;
    if (t.status === "new") untriaged += 1;
    // A ticket awaiting re-test is still in flight: the fix is deployed but
    // not confirmed, so it is counted with the work rather than dropped.
    if (t.status === "in_progress" || t.status === "retest") inProgress += 1;
    if (t.kind === "bug") openBugs += 1;
    if (t.severity === "high" || t.severity === "critical") openUrgent += 1;
    const age = ticketAgeDays(t, nowMs);
    if (age !== null && (oldestOpen === null || age > oldestOpen)) {
      oldestOpen = age;
    }
  }

  return { open, untriaged, inProgress, openBugs, openUrgent, oldestOpen };
};

export default feedbackStats;
